/**
 * Blog plan + credit ledger (Stripe add-on).
 * Persisted under `template_settings.tweaks.blogCredits`.
 * Entitlement is detected from `user.stripe_detail` (same pattern as Website).
 */

import { StaticVariables } from "../StaticVariables";
import {
  BLOG_LIVE_PRODUCT_IDS,
  getBlogPriceId,
  getBlogProductId,
  getBlogProductIds,
  isStripeTestModeSync,
  type BlogStripePlan,
} from "../StripeTestMode";
import {
  loadTemplateSettings,
  saveTemplateSettings,
} from "./TemplateDatabase";

export type BlogPlanKey = BlogStripePlan;

export const BLOG_PLAN_ALLOWANCES: Record<
  BlogPlanKey,
  {
    postsPerMonth: number;
    generationCredits: number;
    publishCredits: number;
    label: string;
    priceLabel: string;
    description: string;
  }
> = {
  blog_4: {
    postsPerMonth: 4,
    generationCredits: 16,
    publishCredits: 4,
    label: "4 posts / month",
    priceLabel: "Subscribe",
    description:
      "16 generation credits and 4 publishing credits each month. Weekly roundup uses 1 generation credit per post.",
  },
  blog_12: {
    postsPerMonth: 12,
    generationCredits: 48,
    publishCredits: 12,
    label: "12 posts / month",
    priceLabel: "Subscribe",
    description:
      "48 generation credits and 12 publishing credits each month. Every-other-day cadence uses about 3 generation credits per week.",
  },
};

export type BlogGenerationSpendKind =
  | "manual"
  | "weekly_roundup"
  | "ai_best_post"
  | "regenerate";

/** Where a credit event came from (audit trail). */
export type BlogCreditSpendSource =
  | "dashboard_settings"
  | "dashboard_compose"
  | "dashboard_prompt"
  | "content_bank"
  | "content_picker"
  | "section_studio"
  | "mte_generate"
  | "approval_publish"
  | "editor_publish"
  | "cron_reconcile"
  | "checkout_grant"
  | "month_reset"
  | "plan_sync"
  | (string & {});

export type BlogCreditUsageType = "generation" | "publish" | "grant";

/** Append-only usage row persisted under `tweaks.blogCredits.usage`. */
export type BlogCreditUsageEvent = {
  /** Idempotency key — duplicate ids are ignored. */
  id: string;
  at: string;
  type: BlogCreditUsageType;
  /** Credits deducted (0 when waived / free regen / grant metadata). */
  cost: number;
  /** Admin bypass — tracked but balance unchanged. */
  waived?: boolean;
  kind?:
    | BlogGenerationSpendKind
    | "publish"
    | "plan_grant"
    | "month_reset"
    | "upgrade_grant";
  source: BlogCreditSpendSource;
  blogId?: string | null;
  title?: string | null;
};

export type BlogCreditsState = {
  plan: BlogPlanKey | null;
  generationRemaining: number;
  publishRemaining: number;
  /** ISO date (UTC) for the start of the current credit period. */
  periodStart: string | null;
  lastSyncedAt?: string | null;
  /** Newest-first usage ledger (capped). */
  usage: BlogCreditUsageEvent[];
};

export const BLOG_CREDITS_USAGE_LIMIT = 120;

export const DEFAULT_BLOG_CREDITS: BlogCreditsState = {
  plan: null,
  generationRemaining: 0,
  publishRemaining: 0,
  periodStart: null,
  lastSyncedAt: null,
  usage: [],
};

export function isBlogPlanKey(value: unknown): value is BlogPlanKey {
  return value === "blog_4" || value === "blog_12";
}

function parseStripeDetail(raw: unknown): any | null {
  if (!raw) return null;
  if (typeof raw === "object") return raw;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return null;
}

function activeStripeSubs(userData: any): any[] {
  const parsed = parseStripeDetail(userData?.stripe_detail);
  if (!parsed) return [];
  const list = Array.isArray(parsed.subscriptions)
    ? parsed.subscriptions
    : parsed.subscription
      ? [parsed.subscription]
      : [];
  return list.filter((sub) => {
    const status = String(sub?.subscription_status || sub?.status || "").toLowerCase();
    const plan = String(sub?.plan || "").toLowerCase();
    return !status.includes("cancel") && plan !== "cancelled";
  });
}

function normalizeProductToken(value: unknown): string {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");
}

/** Match Stripe line items / plan names / product ids to a Blog plan. */
export function blogPlanKeyFromStripeRef(value: unknown): BlogPlanKey | null {
  const raw = String(value || "").trim();
  if (!raw) return null;
  if (isBlogPlanKey(raw)) return raw;

  const ids = getBlogProductIds();
  if (raw === ids.blog_4 || raw === BLOG_LIVE_PRODUCT_IDS.blog_4) return "blog_4";
  if (raw === ids.blog_12 || raw === BLOG_LIVE_PRODUCT_IDS.blog_12) {
    return "blog_12";
  }

  const token = normalizeProductToken(raw);
  if (
    /\bblog\b/.test(token) &&
    (/\b12\b/.test(token) || token.includes("twelve"))
  ) {
    return "blog_12";
  }
  if (
    /\bblog\b/.test(token) &&
    (/\b4\b/.test(token) || token.includes("four"))
  ) {
    return "blog_4";
  }
  return null;
}

/**
 * Highest Blog add-on on an active Stripe subscription.
 * Prefers 12/mo when both somehow appear.
 */
export function resolveBlogPlanFromStripe(userData: any): BlogPlanKey | null {
  let found: BlogPlanKey | null = null;
  for (const sub of activeStripeSubs(userData)) {
    const candidates: unknown[] = [
      sub?.plan,
      sub?.product_id,
      sub?.metadata?.planName,
      sub?.metadata?.blog_plan,
    ];
    const items = Array.isArray(sub?.items) ? sub.items : [];
    for (const item of items) {
      candidates.push(item?.name, item?.product_id, item?.price_id, item?.plan);
    }
    for (const c of candidates) {
      const key = blogPlanKeyFromStripeRef(c);
      if (!key) continue;
      if (key === "blog_12") return "blog_12";
      found = found || key;
    }
  }
  return found;
}

function normalizeUsageEvent(raw: unknown): BlogCreditUsageEvent | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const id = String(o.id || "").trim();
  const at = String(o.at || "").trim();
  const type = o.type;
  if (!id || !at) return null;
  if (type !== "generation" && type !== "publish" && type !== "grant") return null;
  const cost = Number(o.cost);
  return {
    id,
    at,
    type,
    cost: Number.isFinite(cost) ? Math.max(0, Math.floor(cost)) : 0,
    ...(o.waived ? { waived: true } : {}),
    ...(typeof o.kind === "string" && o.kind.trim()
      ? { kind: o.kind as BlogCreditUsageEvent["kind"] }
      : {}),
    source: (typeof o.source === "string" && o.source.trim()
      ? o.source
      : "unknown") as BlogCreditSpendSource,
    ...(o.blogId != null && String(o.blogId).trim()
      ? { blogId: String(o.blogId).trim() }
      : {}),
    ...(typeof o.title === "string" && o.title.trim()
      ? { title: o.title.trim().slice(0, 160) }
      : {}),
  };
}

function normalizeUsageList(raw: unknown): BlogCreditUsageEvent[] {
  if (!Array.isArray(raw)) return [];
  const out: BlogCreditUsageEvent[] = [];
  const seen = new Set<string>();
  for (const row of raw) {
    const ev = normalizeUsageEvent(row);
    if (!ev || seen.has(ev.id)) continue;
    seen.add(ev.id);
    out.push(ev);
    if (out.length >= BLOG_CREDITS_USAGE_LIMIT) break;
  }
  return out;
}

export function normalizeBlogCredits(raw: unknown): BlogCreditsState {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return { ...DEFAULT_BLOG_CREDITS, usage: [] };
  }
  const o = raw as Record<string, unknown>;
  const plan = isBlogPlanKey(o.plan) ? o.plan : null;
  const gen = Number(o.generationRemaining);
  const pub = Number(o.publishRemaining);
  return {
    plan,
    generationRemaining: Number.isFinite(gen) ? Math.max(0, Math.floor(gen)) : 0,
    publishRemaining: Number.isFinite(pub) ? Math.max(0, Math.floor(pub)) : 0,
    periodStart:
      typeof o.periodStart === "string" && o.periodStart.trim()
        ? o.periodStart
        : null,
    lastSyncedAt:
      typeof o.lastSyncedAt === "string" && o.lastSyncedAt.trim()
        ? o.lastSyncedAt
        : null,
    usage: normalizeUsageList(o.usage),
  };
}

/** Newest-first append; drops duplicate ids; caps length. */
export function appendBlogCreditUsage(
  credits: BlogCreditsState,
  events: BlogCreditUsageEvent | BlogCreditUsageEvent[],
): BlogCreditsState {
  const incoming = Array.isArray(events) ? events : [events];
  if (!incoming.length) return credits;
  const seen = new Set(credits.usage.map((e) => e.id));
  const added: BlogCreditUsageEvent[] = [];
  for (const ev of incoming) {
    const normalized = normalizeUsageEvent(ev);
    if (!normalized || seen.has(normalized.id)) continue;
    seen.add(normalized.id);
    added.push(normalized);
  }
  if (!added.length) return credits;
  return {
    ...credits,
    usage: [...added, ...credits.usage].slice(0, BLOG_CREDITS_USAGE_LIMIT),
    lastSyncedAt: new Date().toISOString(),
  };
}

export function hasBlogCreditUsageId(
  credits: BlogCreditsState,
  id: string,
): boolean {
  const key = String(id || "").trim();
  if (!key) return false;
  return credits.usage.some((e) => e.id === key);
}

export function generationUsageId(blogId: string | number): string {
  return `gen:${String(blogId).trim()}`;
}

export function publishUsageId(blogId: string | number): string {
  return `pub:${String(blogId).trim()}`;
}

function newEphemeralUsageId(prefix: string): string {
  const rand =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  return `${prefix}:${rand}`;
}

/** Usage rows in the current credit period (newest first). */
export function usageEventsForCurrentPeriod(
  credits: BlogCreditsState,
): BlogCreditUsageEvent[] {
  const start = credits.periodStart ? Date.parse(credits.periodStart) : NaN;
  if (!Number.isFinite(start)) return credits.usage.slice();
  return credits.usage.filter((e) => {
    const t = Date.parse(e.at);
    return Number.isFinite(t) && t >= start;
  });
}

function utcMonthStartIso(d = new Date()): string {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)).toISOString();
}

function isSameUtcMonth(iso: string | null | undefined, d = new Date()): boolean {
  if (!iso) return false;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return false;
  const then = new Date(t);
  return (
    then.getUTCFullYear() === d.getUTCFullYear() &&
    then.getUTCMonth() === d.getUTCMonth()
  );
}

/**
 * Align persisted credits with Stripe entitlement.
 * Resets allowances when the plan changes or the UTC month rolls over.
 * Preserves the usage ledger across resets.
 */
export function syncBlogCreditsWithStripe(
  stored: unknown,
  userData: any,
): BlogCreditsState {
  const current = normalizeBlogCredits(stored);
  const stripePlan = resolveBlogPlanFromStripe(userData);
  const plan = stripePlan || current.plan;
  if (!plan) {
    return { ...DEFAULT_BLOG_CREDITS, usage: current.usage };
  }
  const allowance = BLOG_PLAN_ALLOWANCES[plan];
  const needsReset =
    current.plan !== plan ||
    !isSameUtcMonth(current.periodStart) ||
    current.periodStart == null;
  if (!needsReset) {
    return {
      ...current,
      plan,
      lastSyncedAt: new Date().toISOString(),
    };
  }
  const periodStart = utcMonthStartIso();
  const resetReason =
    current.plan && current.plan !== plan ? "plan_sync" : "month_reset";
  const granted = appendBlogCreditUsage(
    {
      plan,
      generationRemaining: allowance.generationCredits,
      publishRemaining: allowance.publishCredits,
      periodStart,
      lastSyncedAt: new Date().toISOString(),
      usage: current.usage,
    },
    {
      id: `grant:${resetReason}:${periodStart}:${plan}`,
      at: new Date().toISOString(),
      type: "grant",
      cost: 0,
      kind: resetReason === "month_reset" ? "month_reset" : "plan_grant",
      source: resetReason,
      title: `Granted ${allowance.generationCredits} generation · ${allowance.publishCredits} publishing`,
    },
  );
  return granted;
}

/**
 * True when the creator has an active Blog Stripe plan (or synced credits).
 * `blog_feature` only gates sidebar visibility — not generate/publish.
 */
export function isBlogCreditsEnforced(
  credits: BlogCreditsState,
  userData?: any,
): boolean {
  return Boolean(credits.plan || resolveBlogPlanFromStripe(userData));
}

export function hasActiveBlogPlan(
  credits: BlogCreditsState,
  userData?: any,
): boolean {
  return isBlogCreditsEnforced(credits, userData);
}

/**
 * Admin always. Creators need an active Blog plan + a publish credit.
 */
export function isBlogPublishingAllowed(opts: {
  isAdmin?: boolean;
  credits: BlogCreditsState;
  userData?: any;
}): boolean {
  if (opts.isAdmin) return true;
  if (!hasActiveBlogPlan(opts.credits, opts.userData)) return false;
  return opts.credits.publishRemaining > 0;
}

/** Admin always. Creators need an active Blog plan to generate (regen is free with a plan). */
export function isBlogGenerationAllowed(opts: {
  isAdmin?: boolean;
  credits: BlogCreditsState;
  userData?: any;
}): boolean {
  if (opts.isAdmin) return true;
  return hasActiveBlogPlan(opts.credits, opts.userData);
}

/** Credits to spend for one generation action. Regenerate is always free. */
export function generationCreditCost(kind: BlogGenerationSpendKind): number {
  if (kind === "regenerate") return 0;
  if (kind === "weekly_roundup") return 1;
  if (kind === "ai_best_post") return 1;
  return 1;
}

/** Soft check when enabling cadence (weekly ≈ 1/wk, every-other-day ≈ 3/wk). */
export function cadenceWeeklyGenerationBudget(
  cadence: "off" | "weekly" | "aiBest",
): number {
  if (cadence === "weekly") return 1;
  if (cadence === "aiBest") return 3;
  return 0;
}

export function canSpendGenerationCredits(
  credits: BlogCreditsState,
  cost: number,
  opts?: { isAdmin?: boolean; userData?: any },
): { ok: boolean; message?: string } {
  if (opts?.isAdmin) return { ok: true };
  if (!hasActiveBlogPlan(credits, opts?.userData)) {
    return {
      ok: false,
      message: "Subscribe to a Blog plan to generate posts.",
    };
  }
  // Regenerate (cost 0) is free once they have a plan.
  if (cost <= 0) return { ok: true };
  if (credits.generationRemaining >= cost) return { ok: true };
  return {
    ok: false,
    message:
      credits.generationRemaining <= 0
        ? "You're out of blog generation credits for this month. Upgrade your Blog plan or wait until next month."
        : `Not enough generation credits (need ${cost}, have ${credits.generationRemaining}).`,
  };
}

export function canSpendPublishCredit(
  credits: BlogCreditsState,
  opts?: { isAdmin?: boolean; userData?: any },
): { ok: boolean; message?: string } {
  if (opts?.isAdmin) return { ok: true };
  if (!hasActiveBlogPlan(credits, opts?.userData)) {
    return {
      ok: false,
      message: "Subscribe to a Blog plan to publish posts.",
    };
  }
  if (credits.publishRemaining > 0) return { ok: true };
  return {
    ok: false,
    message:
      "You're out of publishing credits for this month. Upgrade your Blog plan or wait until next month.",
  };
}

export function applyGenerationSpend(
  credits: BlogCreditsState,
  cost: number,
): BlogCreditsState {
  if (cost <= 0) return credits;
  return {
    ...credits,
    generationRemaining: Math.max(0, credits.generationRemaining - cost),
    lastSyncedAt: new Date().toISOString(),
  };
}

export function applyPublishSpend(credits: BlogCreditsState): BlogCreditsState {
  return {
    ...credits,
    publishRemaining: Math.max(0, credits.publishRemaining - 1),
    lastSyncedAt: new Date().toISOString(),
  };
}

export type RecordGenerationSpendInput = {
  kind: BlogGenerationSpendKind;
  quantity?: number;
  source: BlogCreditSpendSource;
  isAdmin?: boolean;
  blogIds?: Array<string | number | null | undefined>;
  titles?: Array<string | null | undefined>;
  at?: string;
};

/**
 * Track + deduct generation credits. Always appends usage (even admin / regen),
 * but only decrements the balance when cost > 0 and not admin.
 */
export function recordGenerationSpend(
  credits: BlogCreditsState,
  input: RecordGenerationSpendInput,
): { credits: BlogCreditsState; recorded: number; deducted: number } {
  const units = Math.max(1, Math.floor(input.quantity ?? 1));
  const unitCost = generationCreditCost(input.kind);
  const at = input.at || new Date().toISOString();
  const waive = Boolean(input.isAdmin) || unitCost <= 0;
  const events: BlogCreditUsageEvent[] = [];
  let skipped = 0;

  for (let i = 0; i < units; i += 1) {
    const blogIdRaw = input.blogIds?.[i];
    const blogId =
      blogIdRaw != null && String(blogIdRaw).trim()
        ? String(blogIdRaw).trim()
        : null;
    const id = blogId
      ? generationUsageId(blogId)
      : newEphemeralUsageId(`gen:${input.source}`);
    if (hasBlogCreditUsageId(credits, id) || events.some((e) => e.id === id)) {
      skipped += 1;
      continue;
    }
    const title = input.titles?.[i];
    events.push({
      id,
      at,
      type: "generation",
      cost: waive ? 0 : unitCost,
      ...(waive ? { waived: true } : {}),
      kind: input.kind,
      source: input.source,
      ...(blogId ? { blogId } : {}),
      ...(typeof title === "string" && title.trim()
        ? { title: title.trim().slice(0, 160) }
        : {}),
    });
  }

  if (!events.length) {
    return { credits, recorded: 0, deducted: 0 };
  }

  let next = appendBlogCreditUsage(credits, events);
  const deducted = waive ? 0 : unitCost * events.length;
  if (deducted > 0) {
    next = applyGenerationSpend(next, deducted);
  }
  return { credits: next, recorded: events.length, deducted };
}

export type RecordPublishSpendInput = {
  source: BlogCreditSpendSource;
  isAdmin?: boolean;
  blogId?: string | number | null;
  title?: string | null;
  at?: string;
};

/** Track + deduct one publishing credit (idempotent per blog id). */
export function recordPublishSpend(
  credits: BlogCreditsState,
  input: RecordPublishSpendInput,
): { credits: BlogCreditsState; recorded: boolean; deducted: number } {
  const blogId =
    input.blogId != null && String(input.blogId).trim()
      ? String(input.blogId).trim()
      : null;
  const id = blogId
    ? publishUsageId(blogId)
    : newEphemeralUsageId(`pub:${input.source}`);
  if (hasBlogCreditUsageId(credits, id)) {
    return { credits, recorded: false, deducted: 0 };
  }
  const waive = Boolean(input.isAdmin);
  const event: BlogCreditUsageEvent = {
    id,
    at: input.at || new Date().toISOString(),
    type: "publish",
    cost: waive ? 0 : 1,
    ...(waive ? { waived: true } : {}),
    kind: "publish",
    source: input.source,
    ...(blogId ? { blogId } : {}),
    ...(typeof input.title === "string" && input.title.trim()
      ? { title: input.title.trim().slice(0, 160) }
      : {}),
  };
  let next = appendBlogCreditUsage(credits, event);
  if (!waive) next = applyPublishSpend(next);
  return { credits: next, recorded: true, deducted: waive ? 0 : 1 };
}

/** Minimal post shape for cron/ledger reconciliation. */
export type BlogCreditReconcilePost = {
  id: number | string;
  title?: string | null;
  created_at?: string | null;
  published_at?: string | null;
  status?: string | null;
  meta_description?: unknown;
  platform?: unknown;
};

function metaRecord(raw: unknown): Record<string, unknown> | null {
  if (!raw) return null;
  if (typeof raw === "object" && !Array.isArray(raw)) {
    return raw as Record<string, unknown>;
  }
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return null;
    }
  }
  return null;
}

/** Cron / scheduled auto-gen markers — safe to backfill without double-charging compose. */
export function cronGenerationKindFromPost(
  post: BlogCreditReconcilePost,
): BlogGenerationSpendKind | null {
  const platform = metaRecord(post.platform);
  const meta = metaRecord(post.meta_description);
  const kind = String(platform?.kind || meta?.kind || "")
    .trim()
    .toLowerCase();
  if (kind === "weekly_roundup") return "weekly_roundup";
  if (kind === "ai_best_post" || kind === "ai_best") return "ai_best_post";
  return null;
}

function postTimeIso(post: BlogCreditReconcilePost): string | null {
  const raw = post.created_at || post.published_at;
  if (!raw) return null;
  const t = Date.parse(String(raw));
  return Number.isFinite(t) ? new Date(t).toISOString() : null;
}

function isPublishedStatus(status?: string | null): boolean {
  const s = String(status || "").trim().toLowerCase();
  return s === "active" || s === "published";
}

/**
 * Backfill ledger + balances from blog_posts created/published this period
 * that cron/auto-gen produced without a client-side spend.
 */
export function reconcileBlogCreditsFromPosts(
  credits: BlogCreditsState,
  posts: BlogCreditReconcilePost[],
  opts?: { isAdmin?: boolean },
): { credits: BlogCreditsState; generationAdded: number; publishAdded: number } {
  let next = credits;
  let generationAdded = 0;
  let publishAdded = 0;
  const periodStartMs = credits.periodStart
    ? Date.parse(credits.periodStart)
    : NaN;

  for (const post of posts) {
    const blogId = String(post.id ?? "").trim();
    if (!blogId) continue;
    const at = postTimeIso(post);
    if (!at) continue;
    const atMs = Date.parse(at);
    if (Number.isFinite(periodStartMs) && atMs < periodStartMs) continue;

    const genKind = cronGenerationKindFromPost(post);
    if (genKind && !hasBlogCreditUsageId(next, generationUsageId(blogId))) {
      const spent = recordGenerationSpend(next, {
        kind: genKind,
        quantity: 1,
        source: "cron_reconcile",
        isAdmin: opts?.isAdmin,
        blogIds: [blogId],
        titles: [post.title || null],
        at,
      });
      next = spent.credits;
      generationAdded += spent.recorded;
    }

    const publishedAt = post.published_at
      ? Date.parse(String(post.published_at))
      : NaN;
    const publishInPeriod =
      isPublishedStatus(post.status) &&
      Number.isFinite(publishedAt) &&
      (!Number.isFinite(periodStartMs) || publishedAt >= periodStartMs);
    if (
      publishInPeriod &&
      !hasBlogCreditUsageId(next, publishUsageId(blogId))
    ) {
      // Auto-publish from cron is the gap we must catch; manual UI publish
      // already records. Idempotent pub:{id} prevents double charge.
      const autoPub =
        Boolean(genKind) ||
        String(metaRecord(post.platform)?.auto_published || "").toLowerCase() ===
          "true" ||
        metaRecord(post.meta_description)?.auto_published === true;
      if (autoPub) {
        const spent = recordPublishSpend(next, {
          source: "cron_reconcile",
          isAdmin: opts?.isAdmin,
          blogId,
          title: post.title || null,
          at: new Date(publishedAt).toISOString(),
        });
        next = spent.credits;
        if (spent.recorded) publishAdded += 1;
      }
    }
  }

  return { credits: next, generationAdded, publishAdded };
}

/**
 * Start Stripe Checkout for a Blog add-on.
 * Uses the same `/api/stripe/create-checkout-session` shape as LiB plans.
 */
export async function createBlogCheckoutSession(input: {
  plan: BlogPlanKey;
  userId?: string;
  email?: string;
  successPath?: string;
  cancelPath?: string;
}): Promise<{ url?: string; error?: string }> {
  const API_URL = StaticVariables().API_BASE;
  const testMode = isStripeTestModeSync();
  // Backend appends session_id={CHECKOUT_SESSION_ID} — do not pre-add it.
  const successPath =
    input.successPath || "/dashboard/blog/settings?blog_checkout=1";
  const cancelPath = input.cancelPath || "/dashboard/blog/settings";
  try {
    const productId = getBlogProductId(input.plan);
    const priceId = getBlogPriceId(input.plan).trim();
    // Always send productId (live Blog products). Avoid LiB payment-link path —
    // Blog has no payment links and $0 prices resolve from the product on the API.
    const response = await fetch(`${API_URL}/api/stripe/create-checkout-session`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        productId,
        ...(priceId ? { priceId } : {}),
        planName: input.plan,
        blogPlan: input.plan,
        userId: input.userId,
        email: input.email,
        successUrl: `${window.location.origin}${successPath}`,
        cancelUrl: `${window.location.origin}${cancelPath}`,
        testMode,
        tolt_referral:
          typeof window !== "undefined" ? (window as any).tolt_referral : undefined,
      }),
    });
    if (!response.ok) {
      const text = await response.text().catch(() => "");
      return {
        error:
          text?.slice(0, 180) ||
          "Couldn't start Blog checkout. Confirm Blog Stripe prices are configured on the API.",
      };
    }
    const data = (await response.json().catch(() => ({}))) as { url?: string };
    if (!data?.url) return { error: "No checkout URL received from Stripe." };
    return { url: data.url };
  } catch (e) {
    console.warn("[blogCredits] checkout failed", e);
    return { error: "Couldn't reach the billing service." };
  }
}

/** Grant credits after a verified Blog checkout (optimistic until webhook syncs stripe_detail). */
export function creditsAfterBlogCheckout(
  plan: BlogPlanKey,
  previous?: BlogCreditsState | null,
): BlogCreditsState {
  const allowance = BLOG_PLAN_ALLOWANCES[plan];
  const prev = normalizeBlogCredits(previous || DEFAULT_BLOG_CREDITS);
  const at = new Date().toISOString();
  // Same-month upgrade: keep remaining but bump ceilings by delta when moving 4→12.
  if (
    prev.plan &&
    prev.plan !== plan &&
    isSameUtcMonth(prev.periodStart)
  ) {
    const prevA = BLOG_PLAN_ALLOWANCES[prev.plan];
    const genDelta = allowance.generationCredits - prevA.generationCredits;
    const pubDelta = allowance.publishCredits - prevA.publishCredits;
    const next: BlogCreditsState = {
      plan,
      generationRemaining: Math.max(
        0,
        prev.generationRemaining + Math.max(0, genDelta),
      ),
      publishRemaining: Math.max(
        0,
        prev.publishRemaining + Math.max(0, pubDelta),
      ),
      periodStart: prev.periodStart || utcMonthStartIso(),
      lastSyncedAt: at,
      usage: prev.usage,
    };
    return appendBlogCreditUsage(next, {
      id: `grant:upgrade:${plan}:${at}`,
      at,
      type: "grant",
      cost: 0,
      kind: "upgrade_grant",
      source: "checkout_grant",
      title: `Upgraded to ${allowance.label}`,
    });
  }
  const periodStart = utcMonthStartIso();
  const next: BlogCreditsState = {
    plan,
    generationRemaining: allowance.generationCredits,
    publishRemaining: allowance.publishCredits,
    periodStart,
    lastSyncedAt: at,
    usage: prev.usage,
  };
  return appendBlogCreditUsage(next, {
    id: `grant:checkout:${plan}:${periodStart}`,
    at,
    type: "grant",
    cost: 0,
    kind: "plan_grant",
    source: "checkout_grant",
    title: `Activated ${allowance.label}`,
  });
}

export function blogPlanKeyFromCheckoutSession(session: any): BlogPlanKey | null {
  if (!session) return null;
  return (
    blogPlanKeyFromStripeRef(session?.metadata?.blog_plan) ||
    blogPlanKeyFromStripeRef(session?.metadata?.planName) ||
    blogPlanKeyFromStripeRef(session?.planName) ||
    blogPlanKeyFromStripeRef(session?.plan_details?.name) ||
    // Expanded subscription from verify-checkout-session
    blogPlanKeyFromStripeRef(session?.subscription?.metadata?.blog_plan) ||
    blogPlanKeyFromStripeRef(session?.subscription?.metadata?.planName) ||
    blogPlanKeyFromStripeRef(session?.subscription?.items?.data?.[0]?.price?.product) ||
    null
  );
}

/**
 * Load → mutate → save only `tweaks.blogCredits` against the DB row.
 * Use when a surface (e.g. MTE) must not clobber credits via a stale tweaks session.
 */
export async function mutateBlogCreditsInDb(
  userId: string,
  userData: any,
  mutator: (credits: BlogCreditsState) => BlogCreditsState,
): Promise<{ ok: boolean; credits?: BlogCreditsState; error?: string }> {
  const uid = userId?.trim();
  if (!uid) return { ok: false, error: "Missing user id." };
  try {
    const row = await loadTemplateSettings(uid);
    const tweaks =
      row?.tweaks && typeof row.tweaks === "object" && !Array.isArray(row.tweaks)
        ? ({ ...row.tweaks } as Record<string, unknown>)
        : {};
    const current = syncBlogCreditsWithStripe(tweaks.blogCredits, userData);
    const next = mutator(current);
    const saved = await saveTemplateSettings(uid, {
      tweaks: { ...tweaks, blogCredits: next },
    });
    if (!saved.ok) {
      return { ok: false, error: saved.error || "Couldn't save blog credits." };
    }
    return { ok: true, credits: next };
  } catch (e) {
    console.warn("[blogCredits] mutateBlogCreditsInDb failed", e);
    return { ok: false, error: "Couldn't update blog credits." };
  }
}
